/**
 * Uma CONVERSA VIVA = um módulo Vuex próprio + um `APIChat` próprio.
 *
 * ★ POR QUE ESTE ARQUIVO EXISTE (18/09/2026, pedido do owner)
 *
 * O chat nasceu "uma conversa por vez, por superfície": o `state` é singular
 * (`messages: []`, `selectedConversation`, `chatLoading`) e o `_streamSeq` do
 * `APIChat` é por INSTÂNCIA. Abrir um turno novo ABORTA o anterior, e os dois
 * escrevem na mesma `messages[]` — foi o que pôs dois cartões de `/panorama`
 * empilhados na tela do owner, um deles congelado em "5 de 7 etapas".
 *
 * O que o owner pediu é o modelo do Claude: N conversas vivas ao mesmo tempo,
 * cada uma com seu contexto e seu status. A boa notícia, medida antes de
 * escrever uma linha: **a arquitetura já suporta isso**. O store usa
 * `registerModule` dinâmico e o `APIChat` já recebe `storeNamespace` no
 * construtor — é assim que as TRÊS superfícies (IA CRM, Fee Fixo, ai-framework)
 * convivem hoje. Fazer isso por CONVERSA é o mesmo mecanismo, não uma reescrita.
 *
 * ★ A ARMADILHA, e ela já mordeu esta base uma vez
 *
 * `store/state.js` exporta um OBJETO, não uma função. `{ ...state }` copia as
 * REFERÊNCIAS: `messages`, `toolCalls`, `propostasAbertas`, `insights` e mais
 * uma dúzia de arrays/objetos seriam os MESMOS entre todas as conversas — e
 * duas conversas escreveriam na mesma lista sem nenhum erro aparente. O
 * comentário de `store/index.js` registra o caso análogo, medido em 02/08/2026
 * no módulo fee-fixo: "state de módulo espalhado raso compartilha os slices
 * aninhados".
 *
 * Por isso a fábrica clona de VERDADE (`structuredClone`), e o teste irmão
 * prova o isolamento mutando uma conversa e exigindo a outra intacta — com um
 * controle que demonstra o espalhamento raso falhando, para que ninguém
 * "simplifique" isto de volta.
 *
 * `structuredClone` é seguro aqui pelo mesmo motivo que os units existentes já
 * o usam sobre este state: depois que as IIFEs de `localStorage` resolvem, não
 * sobra função nenhuma nele (função lançaria `DataCloneError`).
 */
import * as actions from './actions'
import * as getters from './getters'
import * as mutations from './mutations'
import state from './state'
import {
  criarStateToolParts,
  toolPartsActions,
  toolPartsGetters,
  toolPartsMutations,
} from './toolParts'
import {
  criarStateArtefato,
  artefatoActions,
  artefatoGetters,
  artefatoMutations,
} from './artefato'
import {
  criarStateBoasVindas,
  boasVindasActions,
  boasVindasGetters,
  boasVindasMutations,
} from './boas-vindas'
import {
  criarStateArtefatosDoWorkspace,
  artefatosDoWorkspaceActions,
  artefatosDoWorkspaceGetters,
  artefatosDoWorkspaceMutations,
} from './artefatos-do-workspace'

/** Prefixo do namespace de uma conversa viva. */
export const PREFIXO_NAMESPACE = 'iaConversa'

/**
 * O namespace Vuex de uma conversa. Determinístico: a mesma conversa devolve
 * sempre o mesmo namespace, então reabrir não duplica módulo.
 */
export function namespaceDaConversa(idConversa) {
  return `${PREFIXO_NAMESPACE}:${idConversa}`
}

/**
 * O `state` FRESCO de uma conversa — nenhuma referência compartilhada.
 *
 * As fábricas de slice (`criarStateToolParts`, `criarStateArtefato`,
 * `criarStateBoasVindas`, `criarStateArtefatosDoWorkspace` e
 * `criarStateHubEAgente` — esta última já embutida no `state` base) existem
 * justamente porque o compartilhamento já aconteceu antes; o `structuredClone`
 * cobre o RESTO do state base, que não tem fábrica.
 *
 * ★ D4 (owner, 21/09/2026): esta lista nasceu com TRÊS fábricas enquanto o módulo
 * estático (`store/index.js`) já espalhava CINCO. `boasVindas` e
 * `artefatosDoWorkspace` ficaram de fora, e o segundo é justamente onde o cartão
 * do `/panorama` é guardado — o owner digitava `/panorama` e nada aparecia, com o
 * console acusando `unknown action type: iaConversa:<uuid>/limparArtefatosDoWorkspace`.
 * Quem acrescentar um slice ao módulo estático acrescenta AQUI também; a suíte
 * `conversa-viva.paridade-de-slices.unit.js` exige a paridade e falha se esquecer.
 */
export function criarStateDaConversa() {
  return {
    ...structuredClone(state),
    ...criarStateToolParts(),
    ...criarStateArtefato(),
    ...criarStateBoasVindas(),
    ...criarStateArtefatosDoWorkspace(),
  }
}

/**
 * O módulo Vuex de UMA conversa. `namespaced: true` pelo mesmo motivo das três
 * superfícies: quem consome parametriza só o namespace.
 */
export function criarModuloDaConversa() {
  return {
    namespaced: true,
    state: criarStateDaConversa(),
    actions: {
      ...actions,
      ...toolPartsActions,
      ...artefatoActions,
      ...boasVindasActions,
      ...artefatosDoWorkspaceActions,
    },
    getters: {
      ...getters,
      ...toolPartsGetters,
      ...artefatoGetters,
      ...boasVindasGetters,
      ...artefatosDoWorkspaceGetters,
    },
    mutations: {
      ...mutations,
      ...toolPartsMutations,
      ...artefatoMutations,
      ...boasVindasMutations,
      ...artefatosDoWorkspaceMutations,
    },
  }
}
